import { IBaseProps } from '../types';
import { CustomerDetailType } from '../../CustomerTab/types';
import React, { useEffect, useMemo, useState } from 'react';
import useForm from '../useForm';
import style from '../style.module.css';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FlagPhonePickerValidationHelper from 'external-components/flag-phone-picker-react/FlagPhonePickerValidationHelper';
import PhoneNumberService from 'external-services/phone-number-service';
import { InputAdornment, withStyles } from '@material-ui/core';
import { APP_COLORS, Colors } from 'design-v2/globals/colors/colors';
import AdminService from 'external-services/diary-admin-service/admin.service';
import classNames from 'classnames';
import LocationCityIcon from '@material-ui/icons/LocationCity';
import { MailOutlined, PersonOutline } from '@material-ui/icons';

const EMPTY_OBJ = {} as any;
const classPrefixStyle = 'BookedByForm';

/**
 * Note: This component is for the Legacy Diary New Booking Dialog only
 * For Diary Mobile and new Diary, use BookedByForBookingCreator.tsx
 */

const BookedByForNewBooking = (props: IBaseProps) => {
  const {
    customer = EMPTY_OBJ,
    handleChange,
    country,
    setErrors,
    mode = 'new',
    triggerBookedByValidation,
    isSurnameRequired,
    classPrefixBookedBy,
    titleSubscription,
  } = props;

  const classPrefix = classPrefixBookedBy || classPrefixStyle;
  const CssTextField = useMemo(
    () =>
      withStyles({
        root: {
          [`& .Mui-error`]: {
            fontSize: '12px',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormHelperText-root`]: {
            fontFamily: 'Roboto, Helvetica Neue, sans-serif',
            letterSpacing: '0',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInputBase-root`]: {
            fontFamily: 'Roboto, Helvetica Neue, sans-serif',
            fontSize: '16px',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInputLabel-shrink`]: {
            color: 'rgba(0, 0, 0, 0.6)',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInput-underline:before`]: {
            borderWidth: '1px !important',
            borderColor: 'rgba(0,0,0,0.12) !important',
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiInput-underline.Mui-error:after`]: {
            borderBottomColor: 'rgba(0, 0, 0, 0.03)',
          },
          [`& .${
            classPrefix ? classPrefix + '-' : ''
          }MuiInput-underline.Mui-error.Mui-focused:after`]: {
            borderBottomColor: Colors.get(APP_COLORS.clrNbiMagenta),
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormLabel-root.Mui-focused`]: {
            color: Colors.get(APP_COLORS.clrNbiMagenta),
          },
          [`& .${classPrefix ? classPrefix + '-' : ''}MuiFormLabel-root.Mui-error`]: {
            color: 'rgba(0, 0, 0, 0.6)',
          },
        },
      })(TextField),
    []
  );

  const {
    values: bookedCus,
    setValue,
    errors,
  } = useForm(customer, triggerBookedByValidation, handleChange, isSurnameRequired);

  useEffect(() => {
    setErrors && setErrors(errors);
  }, [errors]);

  const nationalNumber = useMemo(() => {
    return customer?.phone
      ? PhoneNumberService.parseNumber(customer.phone, true)?.nationalNumber
      : undefined;
  }, [customer]);

  const isSmsEnabledInSetting = AdminService.getVenue().smsEnabled;

  return (
    <div
      className={classNames({
        [style.separateBbInfoIntoTwoColumns]: true,
        [style.customerTabContainer]: true,
      })}
    >
      {/*left side*/}
      <div
        style={{
          display: 'grid',
          rowGap: '4px',
          alignItems: 'stretch',
          gridTemplateColumns: 'auto',
          gridTemplateRows: 'auto',
          width: '48%',
        }}
      >
        <div className={style.inputForBbInNewBooking}>
          <CssTextField
            error={Boolean(errors?.firstName)}
            fullWidth
            name="firstName"
            placeholder={'First Name'}
            helperText={errors?.firstName ? errors?.firstName : ''}
            value={bookedCus?.firstName || ''}
            onChange={setValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline className={style.iconForBbInNewBooking} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={style.inputForBbInNewBooking}>
          <CssTextField
            error={Boolean(errors?.lastName)}
            fullWidth
            name="lastName"
            placeholder={'Surname'}
            helperText={errors?.lastName ? errors?.lastName : ''}
            value={bookedCus?.lastName || ''}
            onChange={setValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline className={style.iconForBbInNewBooking} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={style.phoneSectionForBbInNewBooking}>
          <FlagPhonePickerValidationHelper
            forceTouched={true}
            skipAutoComplete={true}
            customerCountry={country}
            validationError={errors?.phone}
            customerPhone={nationalNumber || ''}
            classPrefix={classPrefix}
            handlePhoneChange={(value: string, phoneWithoutPrefix: string) => {
              setValue({
                target: {
                  name: CustomerDetailType.phone,
                  value,
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            usePlaceholderForLabel={true}
            isDropDownListMoveUp={mode === 'new'}
          />
        </div>

        <div className={style.inputForBbInNewBooking}>
          <CssTextField
            error={Boolean(errors?.email)}
            fullWidth
            name="email"
            placeholder={'Email'}
            helperText={errors?.email ? errors?.email : ''}
            value={bookedCus?.email || ''}
            onChange={setValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlined className={style.iconForBbInNewBooking} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={style.inputForBbInNewBooking}>
          <CssTextField
            fullWidth
            name="company"
            placeholder={'Company'}
            helperText={errors?.company ? errors?.company : ''}
            value={bookedCus?.company || ''}
            onChange={setValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationCityIcon className={style.iconForBbInNewBooking} />
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>

      {/* for right side*/}
      <div
        className={classNames({
          [style.communicationContainerForBbInNewBooking]: true,
          [style.fieldContainer]: true,
        })}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!!bookedCus.subscribed}
              onChange={setValue}
              name="subscribed"
              style={{ color: '#37484F' }}
            />
          }
          label={
            <Typography style={{ fontSize: 16, lineHeight: 1.2 }}>{titleSubscription}</Typography>
          }
          className={style.checkBoxContainer}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!bookedCus.disableEmailSmsNotification}
              onChange={setValue}
              name="disableEmailSmsNotification"
              style={{ color: '#37484F' }}
            />
          }
          label={
            <Typography style={{ fontSize: 16 }}>
              {!isSmsEnabledInSetting
                ? 'Do not send emails to Booked By'
                : 'Do not send emails or SMS to Booked By'}
            </Typography>
          }
        />
      </div>
    </div>
  );
};
export default BookedByForNewBooking;
